<script setup>
  import { computed } from 'vue'
  import BcstProgressBar from './BcstProgressBar.vue'
  import TransformLessonCard from './TransformLessonCard.vue'
  import checkGreenIcon from '@/assets/images/bcst-check-green.svg'
  import checkGrayIcon from '@/assets/images/bcst-check-gray.svg'

  const props = defineProps({
    lessons: { type: Array, required: true },
    module: { type: Object, required: true },
    total: { type: Number, required: true },
    completedCount: { type: Number, required: true },
    percent: { type: Number, required: true },
    // { [lessonId]: resolvedStorageUrl } — pre-resolved by the parent before rendering.
    thumbnailUrls: { type: Object, required: true },
    // Id of the next incomplete lesson (for the "Next in Queue" badge).
    nextInQueueId: { type: String, default: null },
  })

  // Set of video lesson IDs that have at least one quiz depending on them.
  // Used to show the "no associated quiz" note on videos with no quiz counterpart.
  const videoIdsWithQuiz = computed(() => {
    const s = new Set()
    for (const l of props.lessons) {
      if (l.lessonType === 'quiz' && l.quiz?.requiredLessonId) {
        s.add(l.quiz.requiredLessonId)
      }
    }
    return s
  })

  // Map { [lessonId]: per-type display number } — used by locked quiz cards to
  // display "Go to Lesson N". Uses the store's displayNumber so videos and quizzes
  // are numbered independently, consistent with the card titles.
  const lessonIndexMap = computed(() => {
    const m = {}
    props.lessons.forEach((l) => { m[l.id] = l.displayNumber ?? null })
    return m
  })

  const isComplete = computed(() => props.percent >= 100 && props.total > 0)
</script>

<template>
  <div class="tlcg-root">
    <!-- ── Section title: full width ── -->
    <h2 class="tlcg-section-title">
      {{ module.title }} ({{ total }})
    </h2>

    <!-- ── Rule text (left) + progress bar (right) in the same row ── -->
    <div class="row align-items-start g-3 mb-4 tlcg-info-row">
      <div class="col-12 col-lg">
        <p class="tlcg-rule-text mb-0">
          Lessons can be taken in any order. Quizzes become unlocked and can be taken after their associated lesson has been completed.
        </p>
      </div>
      <div class="col-12 col-lg-auto d-flex align-items-center justify-content-start justify-content-lg-end">
        <span class="text-16 fw-bold flex-shrink-0 me-3 tlcg-percent-label">{{ percent }}% Complete</span>
        <BcstProgressBar
          :value="percent"
          :size="{ width: '419px', height: '13.01px' }"
          :isComplete="isComplete"
          iconSize="29px">
          <template #icon>
            <img
              :src="isComplete ? checkGreenIcon : checkGrayIcon"
              alt=""
              class="flex-shrink-0 tlcg-check-icon" />
          </template>
        </BcstProgressBar>
      </div>
    </div>

    <!-- ── Lessons grid: 3 → 2 → 1 columns ── -->
    <div class="row g-4">
      <div
        v-for="(lesson, idx) in lessons"
        :key="lesson.id"
        class="col-12 col-md-6 col-lg-4">
        <TransformLessonCard
          :lesson="lesson"
          :index="lesson.displayNumber ?? idx + 1"
          :thumbnailUrl="thumbnailUrls[lesson.id] ?? null"
          :hasAssociatedQuiz="lesson.lessonType !== 'video' || videoIdsWithQuiz.has(lesson.id)"
          :isNextInQueue="lesson.id === nextInQueueId"
          :lessonIndexMap="lessonIndexMap" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .tlcg-root {
    padding-top: 8px;
  }

  /* Section title: Figma node 21960:115198 — Text - 24 - Bold */
  .tlcg-section-title {
    color: var(--bs-body-color);
    font-family: 'DM Sans';
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%; /* 36px */
    letter-spacing: -0.48px;
    margin-bottom: 8px;
  }

  /* Rule text: Figma — 16px regular, gray-700 #495057, max-width 552px, line-height 130% */
  .tlcg-rule-text {
    max-width: 552px;
    color: var(--bs-gray-700);
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.3;
    letter-spacing: -0.32px;
  }

  .tlcg-percent-label {
    letter-spacing: -0.32px;
    color: var(--bs-body-color);
  }

  /* SVG icon size controlled by iconSize prop (29px) */
  .tlcg-check-icon {
    width: 29px;
    height: 29px;
    display: inline-block;
    line-height: 1;
    margin-left: -5px;
  }
</style>
